import Button from '@monofront/design-system/components/Button';
import Modal from '@monofront/design-system/organisms/Modal';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

interface IConfirmationPopupProps {
  title?: string;
  message: string | React.ReactNode;
  isOpen: boolean;
  confirmButtonLabel: string;
  confirmCancelButtonLabel?: string;
  confirmButtonOnClick: () => void;
  cancelButtonOnClick?: () => void;
  modalClassName?: string;
  hasCancelButton?: boolean;
  children?: React.ReactNode;
  isButtonDisabled?: boolean;
  centeredMessage?: boolean;
}

export const ModalConfirmation: React.FC<IConfirmationPopupProps> = ({
  title,
  children,
  message,
  isOpen,
  confirmButtonLabel,
  confirmCancelButtonLabel,
  confirmButtonOnClick,
  cancelButtonOnClick,
  modalClassName = 'modal-confirmation',
  hasCancelButton = true,
  isButtonDisabled = false,
  centeredMessage = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const tGlobal = useTranslations('Global');

  return (
    <>
      <Modal
        isOpen={isOpen}
        externalToggle={cancelButtonOnClick}
        UNSAFE_className={modalClassName}
        hasPadding
      >
        {title && <h2>{title}</h2>}
        <div className="icon">{children}</div>
        <p className={centeredMessage ? 'tw-text-center' : ''}>{message}</p>
        <div className="modal-cta">
          {hasCancelButton && (
            <Button
              variant="secondary"
              hasMinWidth={false}
              onClick={cancelButtonOnClick}
              dataTestId="ModalConfirmation-Button-cancel"
            >
              {confirmCancelButtonLabel
                ? confirmCancelButtonLabel
                : tGlobal('cancel')}
            </Button>
          )}
          <Button
            onClick={async () => {
              setIsLoading(true);
              try {
                await confirmButtonOnClick();
              } finally {
                setIsLoading(false);
              }
            }}
            isLoading={isLoading}
            hasMinWidth={false}
            disabled={isButtonDisabled}
            dataTestId="ModalConfirmation-Button-confirm"
          >
            {confirmButtonLabel}
          </Button>
        </div>
      </Modal>
      <style jsx>{`
        :global(.modal-confirmation) {
          top: 33%;
          max-width: 560px;
        }

        h2 {
          margin-bottom: var(--space-3);
          font-weight: var(--font-weight-bold);
        }

        .modal-cta {
          margin-top: var(--space-5);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-cta > :global(button) {
          width: fit-content;
          margin: var(--space-1) var(--space-2);
        }

        .modal-cta > :global(button:first-child) {
          margin-right: var(--space-3);
        }

        .icon {
          display: flex;
          justify-content: center;
          margin-bottom: var(--space-2);
        }
      `}</style>
    </>
  );
};
